import React, { useEffect, useState } from 'react';
import { styled } from '@nykaa/nes-ui-components';
import TextField, { STATE, VARIANT } from '@nykaa/nes-ui-components/TextField';
import CameraIcon from '@nykaa/nes-ui-components/Icons/camera';
import ArrowLeft from '@nykaa/nes-ui-components/Icons/arrow-left';
import CrossIcon from '@nykaa/nes-ui-components/Icons/circle-x-duotone';
import { handleHistoryBack } from 'components/BackIcon/helpers';

const Root = styled.div`
  flex: auto;
`;

const BackIcon = styled(ArrowLeft)`
  height: 100%;
  path {
    fill: ${({ theme }) => theme.colors.textSecondary};
  }
  [dir="rtl"] & {
    transform: scaleX(-1);
  }
`;

const CameraIconWrap = styled(CameraIcon)`
  path {
    fill: ${({ theme }) => theme.colors.primary};
  }
`;

const KEY_EVENT_ENTER = 'Enter';

interface Props {
  value: string;
  state: 'default' | 'success' | 'error' | 'focus';
  placeholder: string;
  showBarCodeIcon: boolean;
  searchCallback: (param: string) => void;
  barCodeAction?: () => void;
  handleEnterPress?: (key: string) => void;
}

const SearchBar: React.FC<Props> = ({
  value = '',
  state: defaultState = STATE.default,
  placeholder = '',
  showBarCodeIcon = false,
  searchCallback,
  barCodeAction,
  handleEnterPress,
}: Props) => {
  const [searchKey, setSearchKey] = useState(value);
  const [searchState, setSearchState] = useState(defaultState);

  const handleBarCodeClick = () => {
    if (barCodeAction) {
      barCodeAction();
    }
  };

  const handleChange = (val: string) => {
    setSearchKey(val);
    searchCallback(val);
  };

  const trailingAction = () => {
    if (searchKey) {
      handleChange('');
    } else {
      handleBarCodeClick();
    }
  };

  const handleKeyUp = (event: { key: string }) => {
    if (event.key === KEY_EVENT_ENTER && handleEnterPress) {
      handleEnterPress(searchKey);
    }
  };

  useEffect(() => {
    if (value) {
      setSearchKey(value);
    }
  }, [value]);

  useEffect(() => {
    if (defaultState) {
      setSearchState(defaultState);
    }
  }, [defaultState]);

  const getTralingIcon = () => {
    if (searchKey) {
      return <CrossIcon />;
    }
    if (!searchKey && showBarCodeIcon) {
      return <CameraIconWrap />;
    }
  };

  return (
    <Root>
      <TextField
        variant={VARIANT.compact}
        type="text"
        state={searchState}
        placeholder={placeholder}
        icon={<BackIcon size="20px" />}
        leadingIconAction={handleHistoryBack}
        trailingIcon={getTralingIcon()}
        trailingIconAction={trailingAction}
        value={searchKey}
        onChange={(e: { target: { value: string } }) =>
          handleChange(e.target.value)
        }
        overrides={{
          Root: {
            style: {
              borderRadius: '64px',
            },
          },
        }}
        onKeyUp={handleKeyUp}
      />
    </Root>
  );
};

export default SearchBar;
